import { useEffect, useState } from "react";
import { Mail, Building2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PROFILES } from "@/lib/brand";
import { CATEGORY_META } from "@/i18n/translations";
import { usePerms } from "@/lib/perms";
import { Plus, X } from "lucide-react";

const STATUS = ["new", "in_review", "contacted", "closed"];
const STATUS_LABEL = { new: "Nouveau", in_review: "En cours", contacted: "Contacté", closed: "Clôturé" };

export default function RequestsAdmin() {
  const { can } = usePerms();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", company: "", message: "", locale: "fr" });
  const [saving, setSaving] = useState(false);
  const createRequest = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/admin/requests", draft); toast.success("Demande enregistrée"); setCreating(false); setDraft({ name: "", email: "", company: "", message: "", locale: "fr" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/admin/requests", { params: filter ? { status: filter } : {} }).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (r, status) => { await api.put(`/admin/requests/${r.id}`, { status }); toast.success("Statut mis à jour"); load(); };
  const saveNotes = async (r, notes) => { await api.put(`/admin/requests/${r.id}`, { notes }); toast.success("Note enregistrée"); };
  const del = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/requests/${id}`); load(); };

  return (
    <div className="max-w-5xl">
      <h1 className="mb-1 font-serif text-3xl text-anthracite">Demandes (RSTI Connect)</h1>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-anthracite/50">Sollicitations reçues via le formulaire public ou saisies par l'équipe.</p>
        {can("create") && <button onClick={() => setCreating(true)} data-testid="new-request-btn" className="inline-flex items-center gap-2 bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory hover:bg-black"><Plus className="h-4 w-4" /> Nouvelle demande</button>}
      </div>
      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-anthracite/50 px-4 py-6" data-testid="request-modal">
          <form onSubmit={createRequest} className="flex max-h-[90vh] w-full max-w-lg flex-col bg-white">
            <div className="flex shrink-0 items-center justify-between px-6 pt-6 pb-4"><h2 className="font-serif text-2xl text-anthracite">Nouvelle demande</h2><button type="button" onClick={() => setCreating(false)}><X className="h-5 w-5" /></button></div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6">
              <input required placeholder="Nom" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} data-testid="request-name" className="w-full border border-tertiary px-3 py-2 text-sm outline-none focus:border-champagne" />
              <input required type="email" placeholder="Email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} data-testid="request-email" className="w-full border border-tertiary px-3 py-2 text-sm outline-none focus:border-champagne" />
              <input placeholder="Organisation" value={draft.company} onChange={(e) => setDraft((d) => ({ ...d, company: e.target.value }))} className="w-full border border-tertiary px-3 py-2 text-sm outline-none focus:border-champagne" />
              <textarea required rows={5} placeholder="Besoin exprimé" value={draft.message} onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))} data-testid="request-message" className="w-full border border-tertiary px-3 py-2 text-sm outline-none focus:border-champagne" />
            </div>
            <div className="flex shrink-0 justify-end gap-3 border-t border-tertiary px-6 py-4">
              <button type="button" onClick={() => setCreating(false)} className="border border-tertiary px-5 py-2.5 text-sm hover:bg-sand">Annuler</button>
              <button type="submit" disabled={saving} data-testid="request-save" className="bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory hover:bg-black disabled:opacity-50">{saving ? "…" : "Enregistrer"}</button>
            </div>
          </form>
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`rounded-full border px-4 py-1.5 text-xs ${!filter ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/60"}`}>Tout</button>
        {STATUS.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full border px-4 py-1.5 text-xs ${filter === s ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/60"}`}>{STATUS_LABEL[s]}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-tertiary bg-white py-16 text-center text-sm text-anthracite/40" data-testid="requests-empty">Aucune demande.</div>
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`request-${r.id}`} className="border border-tertiary bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-anthracite">{r.name}</p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-anthracite/50">
                    <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{r.email}</span>
                    {r.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{r.company}</span>}
                    {r.category && <span className="text-champagne">{CATEGORY_META[r.category]?.fr || r.category}</span>}
                    {r.profile && <span className="rounded bg-anthracite px-2 py-0.5 text-[11px] text-ivory" data-testid={`request-profile-${r.id}`}>{PROFILES.find((p) => p.key === r.profile)?.fr[0] || r.profile}</span>}
                    {r.needs?.length > 0 && <span className="text-anthracite/70">{r.needs.join(" · ")}</span>}
                    <span>{new Date(r.created_at).toLocaleString("fr")}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {can("update") ? (
                    <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid={`request-status-${r.id}`} className="border border-tertiary px-2 py-1.5 text-xs outline-none focus:border-champagne">
                      {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  ) : <span className="text-xs text-anthracite/60">{STATUS_LABEL[r.status]}</span>}
                  {can("delete") && (
                    <button onClick={() => del(r.id)} className="rounded p-1.5 text-anthracite/50 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
              <p className="mt-3 whitespace-pre-line border-l-2 border-tertiary pl-4 text-sm text-anthracite/70">{r.message}</p>
              <textarea
                defaultValue={r.notes} placeholder="Notes internes…" rows={2} readOnly={!can("update")}
                onBlur={(e) => e.target.value !== r.notes && saveNotes(r, e.target.value)}
                className="mt-3 w-full border border-tertiary bg-sand px-3 py-2 text-xs outline-none focus:border-champagne"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
